import { CircleUser, ShoppingCart, Search, X } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useCart } from "../pages/CartContext";
import logo from "../pics/AlNoor_Edtech_logo.png";

export default function NavigationBar() {
  const [openDropdown, setOpenDropdown] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const navigate = useNavigate();

  const { cartItems, removeFromCart, currencySymbol, currency } = useCart();

  // ✅ Read price from item.prices[currency] (fixed prices per currency)
  const getUnitPrice = (item) => Number(item?.prices?.[currency]) || 0;

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const unit = getUnitPrice(item);
      const qty = Number(item.quantity) || 1;
      return sum + unit * qty;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, currency]);

  // ✅ Check user auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // ✅ Handle logout
  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setShowUserMenu(false);
    navigate("/login");
  };

  // ✅ Handle profile click (if not logged in)
  const handleProfileClick = () => {
    if (user) {
      setShowUserMenu(!showUserMenu);
    } else {
      navigate("/register");
    }
  };

  // ✅ Search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setShowSearch(false);
      setQuery("");
    }
  };

  const cartCount = cartItems.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);

  return (
    <>
      <header className="flex items-center justify-between px-6 py-4 shadow-md bg-white relative">
        {/* Logo */}
        <div className="flex items-center space-x-1">
          <img src={logo} alt="Logo" className="h-10 w-10 object-contain" />
          <span className="text-xl font-modum tracking-widest [word-spacing:-0.3em]">
            AL NOOR EDTECH
          </span>
        </div>

        {/* Nav Links */}
        <nav className="hidden md:flex space-x-14 text-xl">
          <Link to="/" className="hover:text-brand font-modum tracking-widest">
            HOME
          </Link>
          <Link to="/about" className="hover:text-brand font-modum tracking-widest">
            ABOUT
          </Link>

          {/* Dropdown */}
          <div
            className="relative flex items-center"
            onMouseEnter={() => setOpenDropdown(true)}
            onMouseLeave={() => setOpenDropdown(false)}
          >
            <button
              onClick={() => setOpenDropdown(!openDropdown)}
              className="hover:text-brand font-modum tracking-widest"
            >
              RESOURCES
            </button>
            {openDropdown && (
              <div className="absolute left-0 top-full pt-2 w-52 z-50">
                <div className="bg-white shadow-lg rounded-lg py-2">
                <a href="/books" className="block px-4 py-2 text-sm hover:bg-brand-light">
                  Books
                </a>
                <a href="/resources/courses" className="block px-4 py-2 text-sm hover:bg-brand-light">
                  Courses
                </a>
                <a href="/resources/sessions" className="block px-4 py-2 text-sm hover:bg-brand-light">
                Counseling & Therapy Sessions
                </a>
                <a
                  href="/resources/motherhood-app"
                  className="block px-4 py-2 text-sm hover:bg-brand-light"
                >
                  Motherhood App
                </a>
                </div>
              </div>
            )}
          </div>

          <Link to="/career" className="hover:text-brand font-modum tracking-widest">
            CAREER
          </Link>
          <Link to="/blog" className="hover:text-brand font-modum tracking-widest">
            BLOG
          </Link>
        </nav>

        {/* Icons */}
        <div className="flex items-center space-x-4">
          {/* 🔍 Search */}
          <Search
            className="cursor-pointer hover:text-brand"
            onClick={() => setShowSearch(!showSearch)}
          />

          {/* 👤 User Avatar */}
          <div className="relative">
            {user ? (
              <>
                <div
                  onClick={handleProfileClick}
                  title={user.email}
                  className="w-10 h-10 bg-brand text-black rounded-full flex items-center justify-center cursor-pointer font-semibold"
                >
                  {user.email?.[0]?.toUpperCase()}
                </div>

                {/* User Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-48 z-50">
                    <div className="px-4 py-2 text-sm text-gray-600 border-b truncate">
                      {user.email}
                    </div>
                    <button
                      onClick={() => {
                        navigate("/profile");
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-brand-light"
                    >
                      My Profile
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 hover:bg-brand-light"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </>
            ) : (
              <CircleUser
                className="cursor-pointer hover:text-brand"
                onClick={handleProfileClick}
                title="Login / Register"
              />
            )}
          </div>

          {/* 🛒 Cart */}
          <div className="relative">
            <ShoppingCart
              className="cursor-pointer hover:text-brand"
              onClick={() => setIsCartOpen(true)}
            />
            {cartItems.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-brand text-black text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {cartCount}
              </span>
            )}
          </div>
        </div>

        {/* 🔍 Search Overlay */}
        {showSearch && (
          <div className="absolute inset-0 bg-white flex items-center justify-center">
            <form
              onSubmit={handleSearchSubmit}
              className="flex items-center w-3/4 md:w-1/2 border-2 border-brand rounded-full px-4 py-2"
            >
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search here..."
                className="flex-grow outline-none text-gray-700"
                autoFocus
              />
              <button type="submit" className="ml-2 text-brand font-semibold">
                Search
              </button>
              <X
                className="ml-3 cursor-pointer text-gray-500 hover:text-red-500"
                onClick={() => setShowSearch(false)}
              />
            </form>
          </div>
        )}
      </header>

      {/* 🛒 Cart Drawer */}
      {isCartOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
          <div className="bg-white w-80 sm:w-96 h-full shadow-lg flex flex-col p-5 relative animate-slideIn">
            <button
              onClick={() => setIsCartOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-red-500"
            >
              <X size={20} />
            </button>

            <h2 className="text-2xl font-semibold mb-4">Your Cart</h2>

            {cartItems.length === 0 ? (
              <p className="text-gray-500 text-center mt-20">Your cart is empty 🛍️</p>
            ) : (
              <div className="flex flex-col space-y-4 overflow-y-auto flex-grow">
                {cartItems.map((item, idx) => {
                  const unit = getUnitPrice(item);
                  const qty = Number(item.quantity) || 1;

                  return (
                    <div
                      key={item.id || idx}
                      className="flex items-center justify-between border-b pb-2"
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-12 h-12 object-cover rounded"
                      />

                      <div className="flex-1 ml-3">
                        <p className="font-medium text-gray-800">{item.title}</p>

                        <p className="text-sm text-gray-500">
                          {qty} × {currencySymbol}
                          {unit.toFixed(2)}
                        </p>

                        {!unit && (
                          <p className="text-xs text-red-500 mt-1">
                            Price not available in {currency}
                          </p>
                        )}
                      </div>

                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-auto border-t pt-4">
              <p className="flex justify-between text-lg font-semibold">
                <span>Subtotal:</span>
                <span>
                  {currencySymbol}
                  {subtotal.toFixed(2)}
                </span>
              </p>

              <button
                onClick={() => {
                  setIsCartOpen(false);
                  navigate("/checkout");
                }}
                className="mt-4 w-full bg-brand text-black rounded-xl hover:bg-black hover:text-brand transition-all duration-300 py-2 font-semibold"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
